import type { PlayerData, Letter, LetterKind } from '@/types/generated';

/**
 * Disconnect reason strings the server may include in WebSocket Error messages.
 * App.vue listens for these to trigger logout when a session expires mid-game.
 * Values must match `disconnect_reason` constants in server/protocol/messages.rs.
 */
export const WS_DISCONNECT_REASONS = {
  SESSION_EXPIRED: 'session_expired',
  AUTHENTICATION_REQUIRED: 'authentication_required',
} as const;

const NAVIGATION_STATES = [
  'homepage',
  'login',
  'signup',
  'profile',
  'forgot-password',
  'reset-password',
  'about',
  'privacy',
  'terms',
] as const;
const MATCHMAKING_STATES = [
  'searching',
  'extended-search',
  'match-found',
  'finished',
  'timeout-error',
  'connection-error',
] as const;

/** Navigation states within the matchmaking/homepage screen */
export type NavigationState = (typeof NAVIGATION_STATES)[number];

/** Matchmaking flow states */
export type MatchmakingState = (typeof MATCHMAKING_STATES)[number];

/** All states for the matchmaking screen */
export type MatchmakingScreenState = NavigationState | MatchmakingState;

/**
 * Type guards for runtime type checking
 */

/**
 * Checks if a value is a valid LetterKind
 */
export function isLetterKind(value: unknown): value is LetterKind {
  return value === 'Vowel' || value === 'Consonant';
}

/**
 * Checks if a value is a valid Letter object
 */
export function isLetter(value: unknown): value is Letter {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as any).char === 'string' &&
    isLetterKind((value as any).kind)
  );
}

/**
 * Checks if a value is a valid Player (PlayerData) object
 */
export function isPlayer(value: unknown): value is PlayerData {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as any).id === 'string' &&
    typeof (value as any).name === 'string' &&
    typeof (value as any).is_bot === 'boolean'
  );
}
